import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "./client";

export type FeedbackKind = "bug" | "feature" | "other";

export type FeedbackStatus =
  | "pending"
  | "triaged"
  | "planned"
  | "in_progress"
  | "deployed"
  | "wont_do"
  | "duplicate";

export interface FeedbackHistoryEntry {
  from_status: string | null;
  to_status: string;
  note: string | null;
  created_at: string;
}

export interface MyFeedbackRow {
  id: number;
  kind: FeedbackKind;
  title: string;
  body: string;
  status: FeedbackStatus;
  user_msg: string | null;
  page_url: string | null;
  created_at: string;
  updated_at: string;
  history: FeedbackHistoryEntry[];
}

export interface SubmitFeedbackIn {
  kind: FeedbackKind;
  title: string;
  body: string;
  page_url?: string | null;
}

export function useSubmitFeedback() {
  const qc = useQueryClient();
  return useMutation<{ id: number }, Error, SubmitFeedbackIn>({
    mutationFn: async (body) => (await api.post<{ id: number }>("/feedback", body)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["my-feedback"] });
    },
  });
}

export function useMyFeedback() {
  return useQuery<MyFeedbackRow[]>({
    queryKey: ["my-feedback"],
    queryFn: async () => (await api.get<MyFeedbackRow[]>("/me/feedback")).data,
    staleTime: 30_000,
  });
}

// ---- Admin -----------------------------------------------------------------
export interface AdminInboxRow {
  id: number;
  user_id: number | null;
  user_email: string | null;
  kind: string;
  title: string;
  status: FeedbackStatus;
  cluster_id: number | null;
  cluster_title: string | null;
  created_at: string;
  updated_at: string;
}

export interface AdminFeedbackDetail extends AdminInboxRow {
  body: string;
  page_url: string | null;
  user_agent: string | null;
  admin_note: string | null;
  user_msg: string | null;
}

export interface AdminInboxResp {
  rows: AdminInboxRow[];
  total: number;
}

export interface AdminHistoryEntry {
  id: number;
  from_status: string | null;
  to_status: string;
  by_user_id: number | null;
  by_user_email: string | null;
  note: string | null;
  created_at: string;
}

export interface AdminCluster {
  id: number;
  title: string;
  summary: string;
  proposed_change: string | null;
  status: "open" | "in_progress" | "shipped" | "rejected";
  member_count: number;
  llm_model: string | null;
  llm_run_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface AdminClusterRunResp {
  clusters: AdminCluster[];
  items_considered: number;
}

export interface AdminFeedbackStats {
  by_status: { status: string; count: number }[];
  by_kind_day: { day: string; kind: string; count: number }[];
  median_time_to_triage_seconds: number | null;
  cluster_by_status: { status: string; count: number }[];
}

export interface AdminInboxQuery {
  status?: string[];
  kind?: string[];
  days?: number;
  limit?: number;
  offset?: number;
}

export function useAdminInbox(q: AdminInboxQuery) {
  const params: Record<string, string | number> = {};
  if (q.status && q.status.length) params.status = q.status.join(",");
  if (q.kind && q.kind.length) params.kind = q.kind.join(",");
  if (q.days != null) params.days = q.days;
  if (q.limit != null) params.limit = q.limit;
  if (q.offset != null) params.offset = q.offset;
  return useQuery<AdminInboxResp>({
    queryKey: ["admin-feedback", q],
    queryFn: async () =>
      (await api.get<AdminInboxResp>("/admin/feedback", { params })).data,
    staleTime: 15_000,
  });
}

export function useAdminFeedbackDetail(id: number | null) {
  return useQuery<AdminFeedbackDetail>({
    queryKey: ["admin-feedback-detail", id],
    queryFn: async () =>
      (await api.get<AdminFeedbackDetail>(`/admin/feedback/${id}`)).data,
    enabled: id != null,
    staleTime: 5_000,
  });
}

export function useAdminFeedbackHistory(id: number | null) {
  return useQuery<AdminHistoryEntry[]>({
    queryKey: ["admin-feedback-history", id],
    queryFn: async () =>
      (await api.get<AdminHistoryEntry[]>(`/admin/feedback/${id}/history`)).data,
    enabled: id != null,
    staleTime: 5_000,
  });
}

export interface PatchFeedbackIn {
  status?: FeedbackStatus;
  user_msg?: string;
  admin_note?: string;
  cluster_id?: number;
  unassign_cluster?: boolean;
}

export function usePatchFeedback() {
  const qc = useQueryClient();
  return useMutation<
    AdminFeedbackDetail,
    Error,
    { id: number; body: PatchFeedbackIn }
  >({
    mutationFn: async ({ id, body }) =>
      (await api.patch<AdminFeedbackDetail>(`/admin/feedback/${id}`, body)).data,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["admin-feedback"] });
      qc.invalidateQueries({ queryKey: ["admin-feedback-detail", data.id] });
      qc.invalidateQueries({ queryKey: ["admin-feedback-history", data.id] });
      qc.invalidateQueries({ queryKey: ["admin-feedback-clusters"] });
    },
  });
}

export function useAdminClusters() {
  return useQuery<AdminCluster[]>({
    queryKey: ["admin-feedback-clusters"],
    queryFn: async () =>
      (await api.get<AdminCluster[]>("/admin/feedback/clusters")).data,
    staleTime: 30_000,
  });
}

export function useAdminClusterMembers(clusterId: number | null) {
  return useQuery<AdminInboxRow[]>({
    queryKey: ["admin-cluster-members", clusterId],
    queryFn: async () =>
      (
        await api.get<AdminInboxRow[]>(
          `/admin/feedback/clusters/${clusterId}/members`,
        )
      ).data,
    enabled: clusterId != null,
    staleTime: 15_000,
  });
}

export interface PatchClusterIn {
  title?: string;
  summary?: string;
  proposed_change?: string;
  status?: AdminCluster["status"];
}

export function usePatchCluster() {
  const qc = useQueryClient();
  return useMutation<
    AdminCluster,
    Error,
    { id: number; body: PatchClusterIn }
  >({
    mutationFn: async ({ id, body }) =>
      (await api.patch<AdminCluster>(`/admin/feedback/clusters/${id}`, body)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-feedback-clusters"] });
    },
  });
}

export function useRunClustering() {
  const qc = useQueryClient();
  return useMutation<AdminClusterRunResp, Error, void>({
    mutationFn: async () =>
      (await api.post<AdminClusterRunResp>("/admin/feedback/cluster")).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-feedback-clusters"] });
      qc.invalidateQueries({ queryKey: ["admin-feedback"] });
    },
  });
}

export function useAdminFeedbackStats(days = 90) {
  return useQuery<AdminFeedbackStats>({
    queryKey: ["admin-feedback-stats", days],
    queryFn: async () =>
      (await api.get<AdminFeedbackStats>("/admin/feedback/stats", { params: { days } })).data,
    staleTime: 60_000,
  });
}
